import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * Magnetic button - pill / sharp variants, neon glow
 * Props:
 *  - tone: 'cyan' | 'warm' | 'ghost'
 *  - shape: 'pill' | 'sharp'
 */
export default function MagneticButton({
  children,
  onClick,
  tone = "cyan",
  shape = "pill",
  className = "",
  type = "button",
  disabled = false,
  ...rest
}) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 18, mass: 0.5 });
  const sy = useSpring(y, { stiffness: 220, damping: 18, mass: 0.5 });
  const tx = useTransform(sx, (v) => `${v}px`);
  const ty = useTransform(sy, (v) => `${v}px`);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = e.clientX - (r.left + r.width / 2);
    const py = e.clientY - (r.top + r.height / 2);
    x.set(px * 0.25);
    y.set(py * 0.35);
  };
  const onLeave = () => { x.set(0); y.set(0); };

  const toneStyles = {
    cyan: "bg-white text-bg-0 hover:shadow-[0_0_50px_rgba(0,240,255,0.45)]",
    warm: "bg-gradient-to-r from-neon-orange via-neon-magenta to-neon-purple text-white hover:shadow-[0_0_60px_rgba(255,0,127,0.45)]",
    ghost: "bg-white/5 text-white border border-white/15 hover:border-white/35 hover:bg-white/10",
  }[tone];

  const shapeCls = shape === "pill" ? "rounded-full" : "rounded-md";

  return (
    <motion.button
      ref={ref}
      type={type}
      onClick={onClick}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      disabled={disabled}
      style={{ x: tx, y: ty }}
      whileTap={{ scale: 0.97 }}
      className={`relative inline-flex items-center justify-center gap-2 px-5 py-2.5 font-semibold transition-shadow duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${shapeCls} ${toneStyles} ${className}`}
      {...rest}
    >
      {children}
    </motion.button>
  );
}
